import { useNavigate, useParams } from 'react-router-dom';
import styles from './channel.module.scss';
import React, { useEffect, useReducer, useState } from 'react';
import {
  WButton,
  WCircularProgress,
  WIconButton,
  WSkeleton,
  WSnackbar,
  WStack,
} from '../../components/wrappers/wrapper';
import { WArticle } from '../../components/prefabs/article/article';
import CloseIcon from '@mui/icons-material/Close';
import ChannelCard from '../../components/prefabs/channel-card/channel-card';
import { IPaginate, TArticle, TChannelCard } from '../../models/common';
import dbService from '../../services/database.service';
import InfiniteScroll from 'react-infinite-scroll-component';
import useDeviceType from 'custom-hooks/useDeviceType';
import useArticle from 'custom-hooks/useArticle';
import useDocumentationArticles from 'custom-hooks/useDocumentationArticles';

const FETCH_LIMIT = 12;
export function ChannelPage() {
  const { isDesktop } = useDeviceType();
  const { channelPath } = useParams();
  const { articles, setArticles, deleteArticle } = useArticle();
  const { filterDocumentationArticles } = useDocumentationArticles();
  const [loading, setLoading] = useState(true);
  const [isSnackbarVisible, setIsSnackbarVisible] = useState(false);
  const [viewType, setViewType] = useState<NonNullable<TArticle['view_type']>>('row');
  const [channelCard, setChannelCard] = useState<TChannelCard | undefined>();
  const [channel, setChannel] = useState<any>(null);
  const [skip, setSkip] = useState(0);

  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const navigate = useNavigate();
  const action = (
    <React.Fragment>
      <WButton
        variant="contained"
        size="small"
        onClick={() => {
          navigate('/login');
        }}
      >
        Login
      </WButton>
      <WIconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={() => setIsSnackbarVisible(false)}
      >
        <CloseIcon fontSize="small" />
      </WIconButton>
    </React.Fragment>
  );

  const getChannel = async () => {
    const [channel] = await dbService.getChannels({
      filter: { path: channelPath },
      relation: ['card.articles.publisher', 'category'],
    });
    setChannel(channel);
    setChannelCard(channel.card as TChannelCard);
    setViewType(channel.article_type || 'row');
  };

  useEffect(() => {
    setSkip(0);
    setLoading(true);
    getChannel().then((_) => fetchArticles(true));
  }, [channelPath]);

  const [hasMore, setHasMore] = useState(true);

  const fetchArticles = async (clear?: boolean) => {
    try {
      if (clear) {
        setArticles([]);
      }
      const articlesData = await dbService.getArticles({
        filter: { 'channel._id': channel?._id },
        sort: { _id: -1 },
        skip: articles.length,
        limit: 10,
        relation: ['publisher', 'channel', 'channel.category'],
      });

      const isDocumentationChannel = channel?.category?.title === 'Documentation';
      const filteredArticles = filterDocumentationArticles(articlesData, isDocumentationChannel);

      if (filteredArticles.length < 10) {
        setHasMore(false);
      }

      setArticles((prev) => [...prev, ...filteredArticles]);
    } catch (error) {
      console.error('Error fetching articles:', error);
    }
  };

  const handleDeleteArticle = (id: string) => {
    deleteArticle(id).then(() => forceUpdate());
  };


  return (
    <div className={`${styles.container} ${styles[viewType]}`}>
      {loading ? (
        <WCircularProgress className={styles.progress} size="1.5rem" />
      ) : (
        <>
          <div className={styles.content}>
            {articles.length ? (
              <InfiniteScroll
                dataLength={articles.length}
                next={fetchArticles}
                hasMore={hasMore}
                loader={<SkeletonArticleCard />}
                height={'var(--acticles-content-height)'}
                className={styles.channel}
              >
                {articles.map((article, index) => {
                  const isLastChild = index === articles.length - 1;
                  return (
                    <div className={styles.article} key={index}>
                      <WArticle
                        data={article}
                        key={article._id}
                        type={viewType}
                        typeRowClass={isLastChild ? styles.cardBorder : ''}
                        onDelete={handleDeleteArticle}
                      />
                    </div>
                  );
                })}
              </InfiniteScroll>
            ) : (
              <h4 className={styles.noContent}>There are no articles yet</h4>
            )}
          </div>
          {isDesktop && !!channelCard?.articles?.length && (
            <ChannelCard data={channelCard} className={styles.channelCard} />
          )}
        </>
      )}

      <WSnackbar
        open={isSnackbarVisible}
        autoHideDuration={3000}
        onClose={() => setIsSnackbarVisible(false)}
        message="In order to carry out this action, it is necessary to log in first."
        action={action}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      />
    </div>
  );
}

function SkeletonArticleCard() {
  return (
    <WStack spacing={2}>
      <WSkeleton variant="rounded" height={335}></WSkeleton>
      <WSkeleton variant="rounded" height={335}></WSkeleton>
    </WStack>
  );
}
